import { z } from 'zod';

import { calendarDateSchema, idSchema } from './common';
import { personSchema } from './identity';
import { LIMITS } from './limits';
import { UNASSIGNED } from './filters/model';

/** Global pages: My Work and the Dashboard (api-design §3.7, MYW-1..4, DSH-1..8). */

/** A work item as the global pages list it: enough to show a row and open its card. */
export const workItemSummarySchema = z.object({
  id: idSchema,
  key: z.string(),
  title: z.string(),
  projectId: idSchema,
  projectName: z.string(),
  parentId: idSchema.nullable(),
  statusId: idSchema,
  priorityId: idSchema.nullable(),
  dueDate: calendarDateSchema.nullable(),
  ownerIds: z.array(idSchema),
});

export const myWorkQuerySchema = z.object({ includeDone: z.stringbool().optional() });

export const myWorkResponseSchema = z.object({
  /** The viewer's calendar date: sections are relative to it (MYW-2). */
  today: calendarDateSchema,
  items: z.array(workItemSummarySchema),
});

/** Comma-separated list in a query string (`?projectIds=a,b`). */
const csv = <T extends z.ZodType>(item: T) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value !== '' ? value.split(',') : undefined),
    z.array(item).max(200).optional(),
  );

export const DASHBOARD_DUE = ['all', 'overdue', 'thisWeek', 'thisMonth', 'range'] as const;

export const dashboardQuerySchema = z.object({
  projectIds: csv(idSchema),
  ownerIds: csv(z.union([idSchema, z.literal(UNASSIGNED)])),
  q: z.string().max(LIMITS.searchQueryMax).optional(),
  due: z.enum(DASHBOARD_DUE).optional(),
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});

const categoryCounts = z.object({
  notStarted: z.number().int(),
  inProgress: z.number().int(),
  stuck: z.number().int(),
  done: z.number().int(),
});

export const dashboardResponseSchema = z.object({
  today: calendarDateSchema,
  /** The projects the filter can choose from: visible, active. */
  projects: z.array(z.object({ id: idSchema, name: z.string() })),
  /** DSH-3, counted over work items (tasks and subtasks). */
  totals: z.object({
    all: z.number().int(),
    inProgress: z.number().int(),
    stuck: z.number().int(),
    done: z.number().int(),
    overdue: z.number().int(),
  }),
  /** DSH-4. */
  byStatus: z.array(z.object({ statusId: idSchema, count: z.number().int() })),
  /** DSH-5: an item with several owners counts once per owner; null is "Unassigned". */
  byOwner: z.array(categoryCounts.extend({ ownerId: idSchema.nullable() })),
  /** DSH-6: most overdue first. */
  overdue: z.array(workItemSummarySchema),
  /** DSH-7: open and overdue counts per person, with their open items by due date. */
  people: z.array(
    z.object({
      ownerId: idSchema.nullable(),
      open: z.number().int(),
      overdue: z.number().int(),
      items: z.array(workItemSummarySchema),
    }),
  ),
});

/** A status category as the Dashboard counts it (DSH-3, DSH-5). */
export const DASHBOARD_CATEGORIES = ['notStarted', 'inProgress', 'stuck', 'done'] as const;

/**
 * D43: the work items behind one number, pie slice or bar of the Dashboard — its filters plus the
 * slice that was clicked. Conditions combine with AND; none means every matching item.
 */
export const dashboardItemsQuerySchema = dashboardQuerySchema.extend({
  category: z.enum(DASHBOARD_CATEGORIES).optional(),
  statusId: idSchema.optional(),
  owner: z.union([idSchema, z.literal(UNASSIGNED)]).optional(),
  overdue: z.stringbool().optional(),
});

export const dashboardItemsResponseSchema = z.object({
  /** How many items match; `items` holds the first ones by due date. */
  total: z.number().int(),
  items: z.array(workItemSummarySchema),
});

/** ADM-3: someone's open work in active projects, before they are deactivated. */
export const openWorkResponseSchema = z.object({ items: z.array(workItemSummarySchema) });

export type WorkItemSummary = z.infer<typeof workItemSummarySchema>;
export type OpenWorkResponse = z.infer<typeof openWorkResponseSchema>;
export type MyWorkResponse = z.infer<typeof myWorkResponseSchema>;
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;
export type DashboardDue = (typeof DASHBOARD_DUE)[number];
export type DashboardCategory = (typeof DASHBOARD_CATEGORIES)[number];
export type DashboardItemsQuery = z.infer<typeof dashboardItemsQuerySchema>;
export type DashboardItemsResponse = z.infer<typeof dashboardItemsResponseSchema>;

// ── Search (SRC-1..3) ───────────────────────────────────────────────────────────────────────────

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(LIMITS.searchQueryMax),
});

export const searchResponseSchema = z.object({
  projects: z.array(z.object({ id: idSchema, name: z.string(), archived: z.boolean() })),
  /** An exact key (WED-123) comes first; archived projects' items rank lower (SRC-3). */
  items: z.array(
    z.object({
      id: idSchema,
      key: z.string(),
      title: z.string(),
      projectId: idSchema,
      projectName: z.string(),
      statusId: idSchema,
      archived: z.boolean(),
    }),
  ),
});

export type SearchResponse = z.infer<typeof searchResponseSchema>;

// ── My team (D46, MOB-10) ────────────────────────────────────────────────────────────────────────

export const ATTENTION_REASONS = ['stuck', 'overdue'] as const;

/**
 * `GET /team/attention`: the stuck or overdue work of a Manager's team (D45), or of everyone for an
 * Admin, by person — only what the viewer may see. `clear` lists the people with nothing waiting.
 */
export const teamAttentionResponseSchema = z.object({
  today: calendarDateSchema,
  people: z.array(
    z.object({
      person: personSchema,
      items: z.array(
        workItemSummarySchema.extend({ reasons: z.array(z.enum(ATTENTION_REASONS)).min(1) }),
      ),
    }),
  ),
  clear: z.array(personSchema),
});

export type TeamAttentionResponse = z.infer<typeof teamAttentionResponseSchema>;
